<template>
  <!--
    订单收货地址模块（对齐 PC OrderAddress + MasterGo H5 设计稿）
    三种可编辑态：
    1. 空态：头部「我的地址」+ 虚线空卡「选择或新增地址」
    2. 列表展开：头部 + 可勾选地址列表 + 新增入口
    3. 已选收起：头部 + 橙色描边已选卡
    只读态：头部无箭头 + 已选卡（不可切换）
  -->
  <div class="order-address-card">
    <!-- ========================= 可操作 ========================= -->
    <template v-if="editable">
      <!-- ---------- 列表展开 / 加载中 ---------- -->
      <div v-if="loading || listExpanded" class="oa-card">
        <!-- 头部：定位图标 + 标题 + 箭头；点击收起列表 -->
        <div class="oa-header" @click="collapseList">
          <div class="oa-header-title">
            <!-- 定位图标：品牌橙 #FF730B -->
            <svg class="oa-pin" viewBox="0 0 20 20" width="20" height="20" aria-hidden="true">
              <path
                d="M1.832 8.417Q1.832 11.588 4.583 15.203Q6.163 17.279 8.325 19.145Q8.372 19.186 8.421 19.223Q9.122 19.75 9.999 19.75Q10.876 19.75 11.576 19.223Q11.626 19.186 11.673 19.145Q13.835 17.279 15.414 15.203Q18.166 11.588 18.166 8.417Q18.166 5.034 15.774 2.642Q13.382 0.25 9.999 0.25Q6.616 0.25 4.224 2.642Q1.832 5.034 1.832 8.417ZM9.523 17.758C8.05 16.486 3.665 12.37 3.665 8.417C3.665 4.919 6.501 2.083 9.999 2.083C13.497 2.083 16.332 4.919 16.332 8.417C16.332 12.37 11.947 16.486 10.475 17.758C10.193 17.97 9.805 17.97 9.523 17.758Z"
                fill="#FF730B" />
              <path
                d="M12.5 8.333C12.5 9.714 11.381 10.833 10 10.833C8.619 10.833 7.5 9.714 7.5 8.333C7.5 6.953 8.619 5.833 10 5.833C11.381 5.833 12.5 6.953 12.5 8.333Z"
                fill="#FF730B" />
            </svg>
            <span class="oa-title-text">{{ $fanyi('我的地址') }}</span>
          </div>
          <!-- 展开态箭头朝上（与设计稿一致） -->
          <svg class="oa-arrow is-up" viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
            <path
              d="M6 13.8c-.128 0-.256-.049-.353-.147a.5.5 0 0 1 0-.707L10.591 8 5.647 3.056a.5.5 0 0 1 .707-.707l5.121 5.121a.75.75 0 0 1 0 1.061L6.353 13.653A.5.5 0 0 1 6 13.8Z"
              fill="#333333" />
          </svg>
        </div>

        <div class="oa-list">
          <!-- 加载骨架 -->
          <template v-if="loading">
            <span class="oa-skeleton-row"></span>
            <span class="oa-skeleton-row"></span>
          </template>
          <template v-else>
            <!-- 地址项：选中 2px 品牌橙边框 -->
            <div
              v-for="(item, index) in list"
              :key="item.id || index"
              class="oa-item"
              :class="{ 'is-active': sameId(item.id, selectedId) }"
              @click="chooseAddress(item)"
            >
              <!-- 勾选框：未选描边 / 选中填充 + 对勾 -->
              <span class="oa-checkbox" :class="{ 'is-checked': sameId(item.id, selectedId) }">
                <svg v-if="sameId(item.id, selectedId)" viewBox="0 0 20 20" width="20" height="20" aria-hidden="true">
                  <path
                    d="M15.72 6.13a1 1 0 0 0-1.25 0L8.59 12.01 5.54 8.96a1 1 0 0 0-1.25 1.25l3.65 3.65a1 1 0 0 0 1.3 0l6.48-6.48a1 1 0 0 0 0-1.25Z"
                    fill="#F9F9F9" />
                </svg>
              </span>
              <div class="oa-item-info">
                <p class="oa-item-name">{{ addressLabel(item) }}</p>
                <p class="oa-item-desc">{{ fullAddress(item) }}</p>
              </div>
            </div>

            <!-- 新增地址入口 -->
            <div class="oa-add" @click.stop="openAddDialog">
              <span class="oa-add-plus">＋</span>
              <span>{{ $fanyi('新增地址') }}</span>
            </div>
          </template>
        </div>
      </div>

      <!-- ---------- 已选中且列表收起 ---------- -->
      <template v-else-if="displayAddress">
        <div class="oa-header-card" @click="expandList">
          <div class="oa-header">
            <div class="oa-header-title">
              <svg class="oa-pin" viewBox="0 0 20 20" width="20" height="20" aria-hidden="true">
                <path
                  d="M1.832 8.417Q1.832 11.588 4.583 15.203Q6.163 17.279 8.325 19.145Q8.372 19.186 8.421 19.223Q9.122 19.75 9.999 19.75Q10.876 19.75 11.576 19.223Q11.626 19.186 11.673 19.145Q13.835 17.279 15.414 15.203Q18.166 11.588 18.166 8.417Q18.166 5.034 15.774 2.642Q13.382 0.25 9.999 0.25Q6.616 0.25 4.224 2.642Q1.832 5.034 1.832 8.417ZM9.523 17.758C8.05 16.486 3.665 12.37 3.665 8.417C3.665 4.919 6.501 2.083 9.999 2.083C13.497 2.083 16.332 4.919 16.332 8.417C16.332 12.37 11.947 16.486 10.475 17.758C10.193 17.97 9.805 17.97 9.523 17.758Z"
                  fill="#FF730B" />
                <path
                  d="M12.5 8.333C12.5 9.714 11.381 10.833 10 10.833C8.619 10.833 7.5 9.714 7.5 8.333C7.5 6.953 8.619 5.833 10 5.833C11.381 5.833 12.5 6.953 12.5 8.333Z"
                  fill="#FF730B" />
              </svg>
              <span class="oa-title-text">{{ $fanyi('我的地址') }}</span>
            </div>
            <svg class="oa-arrow" viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
              <path
                d="M6 13.8c-.128 0-.256-.049-.353-.147a.5.5 0 0 1 0-.707L10.591 8 5.647 3.056a.5.5 0 0 1 .707-.707l5.121 5.121a.75.75 0 0 1 0 1.061L6.353 13.653A.5.5 0 0 1 6 13.8Z"
                fill="#333333" />
            </svg>
          </div>
        </div>

        <!-- 已选卡：2px 品牌橙边框，点击可重新展开切换 -->
        <div class="oa-selected is-clickable" @click="expandList">
          <div class="oa-selected-main">
            <svg class="oa-pin oa-pin-lg" viewBox="0 0 28 28" width="28" height="28" aria-hidden="true">
              <path
                d="M2.565 11.783Q2.565 18.955 11.654 26.803Q11.72 26.86 11.789 26.912Q13.998 28.573 16.207 26.912Q16.276 26.86 16.342 26.803Q25.431 18.955 25.431 11.783Q25.431 7.048 22.083 3.699Q18.734 0.35 13.998 0.35Q9.262 0.35 5.913 3.699Q2.565 7.048 2.565 11.783ZM13.332 24.861C11.27 23.081 5.131 17.317 5.131 11.783C5.131 6.886 9.101 2.917 13.998 2.917C18.895 2.917 22.865 6.886 22.865 11.783C22.865 17.317 16.726 23.081 14.664 24.861C14.27 25.157 13.726 25.157 13.332 24.861Z"
                fill="#FF730B" />
              <path
                d="M17.5 11.667C17.5 13.6 15.933 15.167 14 15.167C12.067 15.167 10.5 13.6 10.5 11.667C10.5 9.734 12.067 8.167 14 8.167C15.933 8.167 17.5 9.734 17.5 11.667Z"
                fill="#FF730B" />
            </svg>
            <p class="oa-selected-name">{{ displayTitle }}</p>
          </div>
          <p class="oa-selected-desc">{{ displayDesc }}</p>
          <p class="oa-selected-switch">{{ $fanyi('点击切换地址') }}</p>
        </div>
      </template>

      <!-- ---------- 未选地址：空态 ---------- -->
      <template v-else>
        <div class="oa-header-card" @click="expandList">
          <div class="oa-header">
            <div class="oa-header-title">
              <svg class="oa-pin" viewBox="0 0 20 20" width="20" height="20" aria-hidden="true">
                <path
                  d="M1.832 8.417Q1.832 11.588 4.583 15.203Q6.163 17.279 8.325 19.145Q8.372 19.186 8.421 19.223Q9.122 19.75 9.999 19.75Q10.876 19.75 11.576 19.223Q11.626 19.186 11.673 19.145Q13.835 17.279 15.414 15.203Q18.166 11.588 18.166 8.417Q18.166 5.034 15.774 2.642Q13.382 0.25 9.999 0.25Q6.616 0.25 4.224 2.642Q1.832 5.034 1.832 8.417ZM9.523 17.758C8.05 16.486 3.665 12.37 3.665 8.417C3.665 4.919 6.501 2.083 9.999 2.083C13.497 2.083 16.332 4.919 16.332 8.417C16.332 12.37 11.947 16.486 10.475 17.758C10.193 17.97 9.805 17.97 9.523 17.758Z"
                  fill="#FF730B" />
                <path
                  d="M12.5 8.333C12.5 9.714 11.381 10.833 10 10.833C8.619 10.833 7.5 9.714 7.5 8.333C7.5 6.953 8.619 5.833 10 5.833C11.381 5.833 12.5 6.953 12.5 8.333Z"
                  fill="#FF730B" />
              </svg>
              <span class="oa-title-text">{{ $fanyi('我的地址') }}</span>
            </div>
            <svg class="oa-arrow" viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
              <path
                d="M6 13.8c-.128 0-.256-.049-.353-.147a.5.5 0 0 1 0-.707L10.591 8 5.647 3.056a.5.5 0 0 1 .707-.707l5.121 5.121a.75.75 0 0 1 0 1.061L6.353 13.653A.5.5 0 0 1 6 13.8Z"
                fill="#333333" />
            </svg>
          </div>
        </div>
        <!-- 虚线空态：有地址则展开列表，无地址则直接打开新增 -->
        <div class="oa-empty" @click="handleEmptyClick">
          <span class="oa-plus">
            <svg viewBox="0 0 32 32" width="32" height="32" aria-hidden="true">
              <circle cx="16" cy="16" r="15" fill="#FFF4EC" />
              <path d="M16 10v12M10 16h12" stroke="#FF730B" stroke-width="2" stroke-linecap="round" />
            </svg>
          </span>
          <p class="oa-empty-text">{{ $fanyi('选择或新增地址') }}</p>
        </div>
      </template>
    </template>

    <!-- ========================= 只读回显 ========================= -->
    <template v-else>
      <div class="oa-header-card is-readonly">
        <div class="oa-header">
          <div class="oa-header-title">
            <svg class="oa-pin" viewBox="0 0 20 20" width="20" height="20" aria-hidden="true">
              <path
                d="M1.832 8.417Q1.832 11.588 4.583 15.203Q6.163 17.279 8.325 19.145Q8.372 19.186 8.421 19.223Q9.122 19.75 9.999 19.75Q10.876 19.75 11.576 19.223Q11.626 19.186 11.673 19.145Q13.835 17.279 15.414 15.203Q18.166 11.588 18.166 8.417Q18.166 5.034 15.774 2.642Q13.382 0.25 9.999 0.25Q6.616 0.25 4.224 2.642Q1.832 5.034 1.832 8.417ZM9.523 17.758C8.05 16.486 3.665 12.37 3.665 8.417C3.665 4.919 6.501 2.083 9.999 2.083C13.497 2.083 16.332 4.919 16.332 8.417C16.332 12.37 11.947 16.486 10.475 17.758C10.193 17.97 9.805 17.97 9.523 17.758Z"
                fill="#FF730B" />
              <path
                d="M12.5 8.333C12.5 9.714 11.381 10.833 10 10.833C8.619 10.833 7.5 9.714 7.5 8.333C7.5 6.953 8.619 5.833 10 5.833C11.381 5.833 12.5 6.953 12.5 8.333Z"
                fill="#FF730B" />
            </svg>
            <span class="oa-title-text">{{ $fanyi('我的地址') }}</span>
          </div>
        </div>
      </div>

      <div class="oa-selected">
        <template v-if="hasReadonlyAddress">
          <div class="oa-selected-main">
            <svg class="oa-pin oa-pin-lg" viewBox="0 0 28 28" width="28" height="28" aria-hidden="true">
              <path
                d="M2.565 11.783Q2.565 18.955 11.654 26.803Q11.72 26.86 11.789 26.912Q13.998 28.573 16.207 26.912Q16.276 26.86 16.342 26.803Q25.431 18.955 25.431 11.783Q25.431 7.048 22.083 3.699Q18.734 0.35 13.998 0.35Q9.262 0.35 5.913 3.699Q2.565 7.048 2.565 11.783ZM13.332 24.861C11.27 23.081 5.131 17.317 5.131 11.783C5.131 6.886 9.101 2.917 13.998 2.917C18.895 2.917 22.865 6.886 22.865 11.783C22.865 17.317 16.726 23.081 14.664 24.861C14.27 25.157 13.726 25.157 13.332 24.861Z"
                fill="#FF730B" />
              <path
                d="M17.5 11.667C17.5 13.6 15.933 15.167 14 15.167C12.067 15.167 10.5 13.6 10.5 11.667C10.5 9.734 12.067 8.167 14 8.167C15.933 8.167 17.5 9.734 17.5 11.667Z"
                fill="#FF730B" />
            </svg>
            <p class="oa-selected-name">{{ readonlyAddressName }}</p>
          </div>
          <p class="oa-selected-desc">{{ readonlyFullAddress }}</p>
        </template>
        <p v-else class="oa-selected-empty">{{ $fanyi('暂无收货地址') }}</p>
      </div>
    </template>

    <!-- 新增地址弹层：字段与地址管理页一致 -->
    <van-popup v-model:show="dialogVisible" position="bottom" round :style="{ height: '85%' }" @closed="resetForm">
      <div class="oa-add-popup">
        <!-- 弹层标题栏 -->
        <div class="oa-add-header">
          <span>{{ $fanyi('新增地址') }}</span>
          <van-icon name="cross" @click="dialogVisible = false" />
        </div>
        <div class="oa-add-body">
          <van-form ref="addressFormRef">
            <!-- 收件人 -->
            <div class="oa-form-label">{{ $fanyi('收件人') }}</div>
            <van-field
              v-model.trim="form.contacts"
              :placeholder="$fanyi('请填写收件人')"
              maxlength="50"
              :rules="[{ required: true, message: $fanyi('不能为空') }]"
            />
            <!-- 邮编 -->
            <div class="oa-form-label">{{ $fanyi('邮编') }}</div>
            <van-field
              v-model.trim="form.zip_code"
              :placeholder="$fanyi('请输入邮编')"
              maxlength="5"
              :rules="[
                { required: true, message: $fanyi('不能为空') },
                { pattern: ZIP_CODE_REG, message: $fanyi('邮编输入错误') },
              ]"
            />
            <!-- 州 -->
            <div class="oa-form-label">{{ $fanyi('州') }}</div>
            <van-field
              v-model.trim="form.state"
              is-link
              readonly
              :placeholder="$fanyi('请选择地区')"
              :rules="[{ required: true, message: $fanyi('不能为空') }]"
              @click="showState = true"
            />
            <!-- 城市 -->
            <div class="oa-form-label">{{ $fanyi('城市') }}</div>
            <van-field
              v-model.trim="form.city"
              :placeholder="$fanyi('请输入城市')"
              maxlength="50"
              :rules="[{ required: true, message: $fanyi('不能为空') }]"
            />
            <!-- 地址 -->
            <div class="oa-form-label">{{ $fanyi('地址') }}</div>
            <van-field
              v-model.trim="form.address"
              :placeholder="$fanyi('请输入收货地址')"
              maxlength="200"
              :rules="[{ required: true, message: $fanyi('不能为空') }]"
            />
            <!-- 手机 -->
            <div class="oa-form-label">{{ $fanyi('手机') }}</div>
            <van-field
              v-model.trim="form.mobile"
              :placeholder="$fanyi('请输入电话')"
              maxlength="20"
              :rules="[
                { required: true, message: $fanyi('不能为空') },
                { pattern: MOBILE_REG, message: $fanyi('请输入正确的手机号') },
              ]"
            />
            <!-- 邮箱 -->
            <div class="oa-form-label">{{ $fanyi('邮箱') }}</div>
            <van-field
              v-model.trim="form.email"
              :placeholder="$fanyi('请输入邮箱')"
              maxlength="60"
              :rules="[
                { required: true, message: $fanyi('不能为空') },
                { validator: emailValidator, message: $fanyi('请输入正确的邮箱地址') },
              ]"
            />
            <!-- 设为默认 -->
            <div class="oa-default-row">
              <span>{{ $fanyi('设置默认地址') }}</span>
              <van-switch v-model="form.isAcquiescent" active-color="#FF730B" size="20px" />
            </div>
          </van-form>
        </div>
        <!-- 底部保存 -->
        <div class="oa-add-footer">
          <div class="oa-save-btn" :class="{ disabled: submitting }" @click="submitAddress">
            {{ $fanyi('确认添加') }}
          </div>
        </div>
      </div>
    </van-popup>

    <!-- 州选择器 -->
    <van-popup v-model:show="showState" round position="bottom">
      <van-picker
        :columns="stateColumns"
        :title="$fanyi('州')"
        :cancel-button-text="$fanyi('取消')"
        :confirm-button-text="$fanyi('确认')"
        @cancel="showState = false"
        @confirm="onStateConfirm"
      />
    </van-popup>
  </div>
</template>

<script setup>
/**
 * 订单「我的地址」选择组件
 * - editable=true：提出订单 / 临时保存 / 修改订单 可选择、新增
 * - editable=false：订单详情等只读回显
 * 选中结果通过 change 事件交给父级，提出订单时带 address_id
 */
import { ref, computed, watch, getCurrentInstance, onMounted, defineProps, defineEmits, defineExpose } from 'vue'
import { showFailToast, showSuccessToast } from 'vant'
import stateList from '@/utlis/allImportData'
import { isValidEmail, ZIP_CODE_REG, MOBILE_REG } from '@/utlis/addressValidate'

const props = defineProps({
  // 父级传入的已选地址 id（用于回显）
  addressId: {
    type: [String, Number],
    default: '',
  },
  // 订单号：可编辑时用于回显订单已保存地址
  orderSn: {
    type: String,
    default: '',
  },
  // 是否可编辑
  editable: {
    type: Boolean,
    default: true,
  },
  // 订单已保存地址对象（consignee_address / receive）
  orderAddress: {
    type: Object,
    default: null,
  },
})

const emit = defineEmits(['change'])
const { proxy } = getCurrentInstance()

const list = ref([]) // 用户收货地址列表
const loading = ref(false) // 列表加载中
const selectedId = ref(props.addressId) // 当前选中地址 id
const listExpanded = ref(false) // 列表是否展开
const remoteOrderAddress = ref(null) // 只读兜底请求到的订单地址
const dialogVisible = ref(false) // 新增地址弹层
const submitting = ref(false) // 新增提交中
const showState = ref(false) // 州选择器
const addressFormRef = ref(null) // 表单 ref

// 新增地址表单：type=1 表示收货地址
const createEmptyForm = () => ({
  type: 1,
  contacts: '',
  zip_code: '',
  state: '',
  city: '',
  address: '',
  mobile: '',
  email: '',
  acquiescent: 0,
  isAcquiescent: false,
})
const form = ref(createEmptyForm())

// 州下拉数据
const stateColumns = ref(
  (stateList.provincesList || []).map((item) => ({ text: item, value: item }))
)

/** 邮箱自定义校验（van-field validator） */
const emailValidator = (val) => isValidEmail(val)

/** id 宽松相等：接口有时 number / string 混用，严格 === 会导致匹配失败 */
const sameId = (a, b) => a != null && b != null && String(a) === String(b)

/** 当前选中的地址对象（必须来自地址簿，才能拿到可提交的 address_id） */
const currentAddress = computed(() => {
  if (!selectedId.value) return null
  return list.value.find((item) => sameId(item.id, selectedId.value)) || null
})

/** 只读模式下展示的地址 */
const readonlyAddress = computed(() => {
  const pick = (addr) => {
    if (!addr || Array.isArray(addr)) return null
    return Object.keys(addr).length ? addr : null
  }
  return pick(props.orderAddress) || pick(remoteOrderAddress.value)
})

/** 只读地址是否有内容 */
const hasReadonlyAddress = computed(() => {
  const addr = readonlyAddress.value
  if (!addr) return false
  return ['contacts', 'address', 'city', 'zip_code'].some(
    (key) => addr[key] !== undefined && addr[key] !== null && String(addr[key]).trim() !== ''
  )
})

/**
 * 可编辑「已选卡」只展示已匹配到地址簿的项
 * 不再用无 id 的订单快照伪装成已选，否则父级拿不到 address_id，提出时会被拦
 */
const displayAddress = computed(() => currentAddress.value)

/** 已选卡标题：地址 N 或收件人 */
const displayTitle = computed(() => {
  const addr = displayAddress.value
  if (!addr) return ''
  const index = list.value.findIndex((item) => sameId(item.id, addr.id))
  if (index > -1) return addressLabel(addr)
  return addr.contacts || proxy.$fanyi('收货地址')
})

/** 已选卡摘要 */
const displayDesc = computed(() => fullAddress(displayAddress.value))

/** 只读标题 */
const readonlyAddressName = computed(() => {
  return (readonlyAddress.value && readonlyAddress.value.contacts) || proxy.$fanyi('我的收货地址')
})

/** 只读摘要 */
const readonlyFullAddress = computed(() => fullAddress(readonlyAddress.value))

// 订单地址异步返回后同步回显，并通知父级写入 address_id
watch(
  () => props.orderAddress,
  (val) => {
    if (!val || !props.editable) return
    matchOrderAddress(val)
    if (selectedId.value) {
      listExpanded.value = false
      emitChange()
    }
  }
)

/**
 * 父级 orderSn 往往晚于组件挂载才有值：
 * 详情接口回来后再兜底拉订单地址 / 重新匹配
 */
watch(
  () => props.orderSn,
  (sn, prev) => {
    if (!sn || sn === prev) return
    if (props.editable) {
      // 列表已加载则直接匹配订单地址；未加载则走完整拉取
      if (list.value.length) {
        loadOrderAddress()
      } else if (!loading.value) {
        loadAddressList()
      }
    } else if (!props.orderAddress) {
      loadOrderAddress()
    }
  }
)

onMounted(() => {
  if (props.editable) {
    loadAddressList()
  } else if (props.orderSn && !props.orderAddress) {
    loadOrderAddress()
  }
})

/** 收起列表 */
const collapseList = () => {
  listExpanded.value = false
}

/** 展开列表 */
const expandList = () => {
  listExpanded.value = true
}

/**
 * 点击空态虚线卡：
 * - 加载中 → 展开骨架
 * - 有地址 → 展开列表
 * - 无地址 → 直接打开新增
 */
const handleEmptyClick = () => {
  if (loading.value) {
    expandList()
    return
  }
  if (list.value.length) {
    expandList()
  } else {
    openAddDialog()
  }
}

/** 拉取用户收货地址列表 */
const loadAddressList = async () => {
  loading.value = true
  try {
    const res = await proxy.$api.useraddressList()
    if (res && res.code !== undefined && res.code != 0) {
      showFailToast(proxy.$fanyi(res.msg || '数据获取错误'))
      list.value = []
      return
    }
    const data = (res && res.data) || {}
    // 只取收货地址 consignee，忽略进口商
    list.value = Array.isArray(data.consignee) ? data.consignee : []

    if (props.orderAddress) {
      matchOrderAddress(props.orderAddress)
    } else if (props.orderSn) {
      await loadOrderAddress()
    }

    // 初始收起：有匹配 → 已选卡；无匹配 → 空态，不自动选默认地址
    listExpanded.value = false
    if (selectedId.value) emitChange()
  } catch (e) {
    list.value = []
    showFailToast(proxy.$fanyi('数据获取错误'))
  } finally {
    loading.value = false
  }
}

/** 获取订单当前地址（回显 / 只读兜底） */
const loadOrderAddress = async () => {
  try {
    const res = await proxy.$api.orderAddress({ order_sn: props.orderSn })
    const data = res && res.data
    if (!data) return
    const picked =
      data.consignee_address && !Array.isArray(data.consignee_address)
        ? data.consignee_address
        : Array.isArray(data)
          ? null
          : data
    if (!picked || !Object.keys(picked).length) return
    remoteOrderAddress.value = picked
    if (props.editable) {
      matchOrderAddress(picked)
      // 匹配成功后同步 address_id 给父级，避免只回显却提交不了
      if (selectedId.value) {
        listExpanded.value = false
        emitChange()
      }
    }
  } catch (e) {
    // 获取失败不影响其它展示
  }
}

/**
 * 把订单地址匹配到用户地址列表
 * 优先 id（宽松比较），其次 联系人+邮编+详细地址
 * 匹配成功后 selectedId 一定是地址簿里的 id，父级才能提交 address_id
 */
const matchOrderAddress = (addr) => {
  if (!addr || Array.isArray(addr) || !Object.keys(addr).length) return
  if (!list.value.length) return
  // id 用宽松比较，兼容 number / string
  if (addr.id != null && addr.id !== '') {
    const byId = list.value.find((item) => sameId(item.id, addr.id))
    if (byId) {
      selectedId.value = byId.id
      return
    }
  }
  const matched = list.value.find(
    (item) =>
      item.contacts == addr.contacts &&
      item.zip_code == addr.zip_code &&
      item.address == addr.address
  )
  if (matched) selectedId.value = matched.id
}

/** 选中地址并收起列表（不请求接口） */
const chooseAddress = (item) => {
  if (!props.editable) return
  selectedId.value = item.id
  listExpanded.value = false
  emitChange()
}

/** 把选中地址同步给父组件 */
const emitChange = () => {
  const address = currentAddress.value
  if (!address) return
  emit('change', address)
}

/** 地址名称：地址 1 / 地址 2 … */
const addressLabel = (item) => {
  const index = list.value.findIndex((it) => sameId(it.id, item.id))
  return proxy.$fanyi('地址') + (index > -1 ? index + 1 : '')
}

/** 地址摘要：城市, 邮编, 详细地址 */
const fullAddress = (item) => {
  if (!item) return ''
  return [item.city, item.zip_code, item.address]
    .filter((part) => part !== undefined && part !== null && part !== '')
    .join(', ')
}

/** 打开新增弹层 */
const openAddDialog = () => {
  if (!props.editable) return
  resetForm()
  dialogVisible.value = true
}

/** 重置新增表单 */
const resetForm = () => {
  form.value = createEmptyForm()
}

/** 州确认 */
const onStateConfirm = ({ selectedOptions }) => {
  showState.value = false
  form.value.state = selectedOptions[0].text
}

/** 提交新增地址 */
const submitAddress = async () => {
  try {
    await addressFormRef.value.validate()
  } catch (e) {
    return
  }

  form.value.acquiescent = form.value.isAcquiescent ? 1 : 0
  submitting.value = true
  try {
    const res = await proxy.$api.useraddressAdd(form.value)
    if (res && res.code !== undefined && res.code != 0) {
      showFailToast(proxy.$fanyi(res.msg || '添加失败'))
      return
    }
    showSuccessToast(proxy.$fanyi(res.msg || '添加成功'))
    dialogVisible.value = false

    const newId = res && res.data && res.data.id
    await loadAddressList()
    if (newId) {
      selectedId.value = newId
      listExpanded.value = false
      emitChange()
    } else if (list.value.length) {
      chooseAddress(list.value[list.value.length - 1])
    }
  } catch (e) {
    showFailToast(proxy.$fanyi('添加失败'))
  } finally {
    submitting.value = false
  }
}

// 暴露给父级：必要时可主动刷新
defineExpose({
  loadAddressList,
  selectedId,
  currentAddress,
})
</script>

<style lang="scss" scoped>
/* token: 品牌色/主色 #FF730B；边框色 #E2E2E2；字体 #303133 / #222 / #888 / #999 */
.order-address-card {
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  /* 与上方商品列表、下方订单备注拉开间距，和页面其它卡片的 10px 节奏一致 */
  margin-top: 10px;
  margin-bottom: 10px;

  /* 列表态整卡 */
  .oa-card {
    width: 100%;
    box-sizing: border-box;
    padding: 0 12px 12px;
    background: #ffffff;
    border: 1px solid #e2e2e2; /* token: 边框色/边框色 */
    border-radius: 8px;
    box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.1); /* token: 投影/默认浮层 */
    display: flex;
    flex-direction: column;
  }

  /* 空态 / 已选态 独立头部卡 */
  .oa-header-card {
    width: 100%;
    height: 60px;
    flex-shrink: 0;
    box-sizing: border-box;
    padding: 0 12px;
    background: #ffffff;
    border: 1px solid #e2e2e2;
    border-radius: 8px;
    cursor: pointer;

    &.is-readonly {
      cursor: default;

      .oa-header {
        cursor: default;
      }
    }
  }

  /* 头部卡与空态/已选卡之间留白，避免视觉上贴在一起 */
  .oa-header-card + .oa-selected,
  .oa-header-card + .oa-empty {
    margin-top: 10px;
  }

  /* 头部行 */
  .oa-header {
    height: 60px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
    user-select: none;

    .oa-header-title {
      display: flex;
      align-items: center;
      gap: 10px;

      .oa-title-text {
        font-family: 'Open Sans', Arial, sans-serif;
        font-size: 14px; /* token: 正文/14S */
        font-weight: 600;
        color: #303133;
        line-height: 20px;
        white-space: nowrap;
      }
    }

    .oa-arrow {
      flex-shrink: 0;
      transform: rotate(90deg); /* 默认朝下 */

      &.is-up {
        transform: rotate(-90deg); /* 展开朝上 */
      }
    }
  }

  /* 地址列表 */
  .oa-list {
    max-height: 220px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 10px;

    &::-webkit-scrollbar {
      width: 0;
    }
  }

  /* 单条地址 */
  .oa-item {
    width: 100%;
    min-height: 64px;
    flex-shrink: 0;
    box-sizing: border-box;
    padding: 12px 16px;
    display: flex;
    align-items: flex-start;
    gap: 10px;
    border: 2px solid #e2e2e2;
    border-radius: 8px;
    background: #ffffff;
    cursor: pointer;
    transition: border-color 0.15s linear;

    &.is-active {
      border-color: #ff730b; /* token: 品牌色/主色 */
    }

    .oa-checkbox {
      width: 20px;
      height: 20px;
      flex-shrink: 0;
      box-sizing: border-box;
      border-radius: 4px;
      background: #ffffff;
      box-shadow: inset 0 0 0 2px #ff730b;
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;

      &.is-checked {
        background: #ff730b;
        box-shadow: none;
      }
    }

    .oa-item-info {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 4px;

      .oa-item-name {
        margin: 0;
        font-size: 14px;
        font-weight: 600;
        color: #222222; /* token: 字体色/#222 */
        line-height: 20px;
      }

      .oa-item-desc {
        margin: 0;
        font-size: 12px; /* token: 提示/12R */
        font-weight: 400;
        color: #888888; /* token: 字体色/#888 */
        line-height: 16px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
  }

  .oa-skeleton-row {
    display: block;
    height: 64px;
    flex-shrink: 0;
    border-radius: 8px;
    background: linear-gradient(90deg, #f5f5f5 25%, #ececec 37%, #f5f5f5 63%);
    background-size: 400% 100%;
    animation: oa-shimmer 1.3s ease-in-out infinite;
  }

  /* 新增入口 */
  .oa-add {
    flex-shrink: 0;
    height: 48px;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    border: 1px dashed #e2e2e2;
    border-radius: 8px;
    font-size: 14px;
    color: #ff730b;
    cursor: pointer;

    .oa-add-plus {
      font-size: 18px;
      line-height: 18px;
    }
  }

  /* 空态虚线卡 */
  .oa-empty {
    width: 100%;
    height: 240px;
    box-sizing: border-box;
    padding: 0 20px;
    background: #ffffff;
    border: 1px dashed #e2e2e2;
    border-radius: 6px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 20px;
    cursor: pointer;
    text-align: center;

    .oa-empty-text {
      margin: 0;
      font-size: 14px; /* token: 正文/14R */
      font-weight: 400;
      color: #2c2c2c;
      line-height: 20px;
    }
  }

  .oa-plus {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  /* 已选卡 */
  .oa-selected {
    width: 100%;
    height: 240px;
    box-sizing: border-box;
    padding: 0 16px;
    background: #ffffff;
    border: 2px solid #ff730b; /* token: 品牌色/主色 */
    border-radius: 6px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    text-align: center;

    &.is-clickable {
      cursor: pointer;
    }

    .oa-selected-switch {
      margin: 0;
      font-size: 12px;
      font-weight: 400;
      color: #999999; /* token: 字体色/#999 */
      line-height: 20px;
    }

    .oa-selected-main {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 20px;
      max-width: 100%;
    }

    .oa-selected-name {
      margin: 0;
      font-size: 14px;
      font-weight: 600;
      color: #303133;
      line-height: 20px;
    }

    .oa-selected-desc {
      margin: 0;
      max-width: 100%;
      font-size: 14px;
      font-weight: 400;
      color: #999999;
      line-height: 20px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .oa-selected-empty {
      margin: 0;
      font-size: 14px;
      font-weight: 400;
      color: #999999;
      line-height: 20px;
    }
  }
}

/* 新增地址弹层 */
.oa-add-popup {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #fff;

  .oa-add-header {
    height: 48px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 16px;
    border-bottom: 1px solid #e2e2e2;
    font-size: 16px;
    font-weight: 600;
    color: #222;
  }

  .oa-add-body {
    flex: 1;
    overflow-y: auto;
    padding: 12px 16px 20px;

    .oa-form-label {
      margin: 12px 0 8px;
      font-size: 12px;
      color: #222;
      line-height: 18px;
    }

    :deep(.van-field) {
      padding: 0;
      border: 0.5px solid #dedede;
      border-radius: 6px;
      padding-left: 10px;
      min-height: 40px;
      align-items: center;
    }

    .oa-default-row {
      margin-top: 20px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      font-size: 14px;
      color: #222;
    }
  }

  .oa-add-footer {
    flex-shrink: 0;
    padding: 10px 16px calc(10px + env(safe-area-inset-bottom));
    border-top: 1px solid #e2e2e2;

    .oa-save-btn {
      height: 44px;
      background: #ff730b;
      border-radius: 6px;
      color: #fff;
      font-size: 14px;
      display: flex;
      align-items: center;
      justify-content: center;

      &.disabled {
        opacity: 0.6;
        pointer-events: none;
      }
    }
  }
}

@keyframes oa-shimmer {
  0% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0 50%;
  }
}

@media (prefers-reduced-motion: reduce) {
  .order-address-card .oa-skeleton-row {
    animation: none;
  }
}
</style>
